<template>
  <section class="ctc-widget-composer">
    <header class="ctc-widget-composer__header">
      <span class="ctc-widget-composer__icon" aria-hidden="true">
        <v-icon :color="colors.indigo">
          {{ icons.mdiViewGridPlusOutline }}
        </v-icon>
      </span>
      <span class="ctc-widget-composer__copy">
        <strong>Widget de produits associés</strong>
        <small>{{ value ? includedText : disabledText }}</small>
      </span>
      <rmr-segmented-control
        :value="value"
        :items="inclusionItems"
        size="sm"
        aria-label="Inclure le widget dans l’article"
        @input="$emit('input', $event)"
      />
    </header>

    <template v-if="value">
      <div class="ctc-widget-composer__toolbar">
        <ctc-widget-format-picker
          :value="widgetType"
          @input="$emit('update:widgetType', $event)"
        />
      </div>

      <div class="ctc-widget-composer__cols">
        <!-- Left: find products. -->
        <div class="ctc-widget-composer__search-col">
          <form
            class="ctc-widget-composer__search"
            role="search"
            @submit.prevent="similarActions.search()"
          >
            <label class="ctc-widget-composer__field">
              <v-icon small :color="colors.muted">
                {{ icons.mdiMagnify }}
              </v-icon>
              <input
                :value="similar.searchTerm"
                type="search"
                name="ctc-product-search"
                autocomplete="off"
                placeholder="Produit, catégorie, EAN ou ASIN"
                aria-label="Rechercher des produits Affilizz"
                @input="similarActions.setSearchTerm($event.target.value)"
              />
            </label>
            <rmr-action-button
              label="Rechercher"
              size="sm"
              type="submit"
              :loading="similar.loading"
              :disabled="similar.searchTerm.trim().length < 2"
            />
          </form>
          <p class="ctc-widget-composer__source">
            {{
              similar.searchTerm.trim()
                ? "Résultats de recherche"
                : "Suggestions pour l’article"
            }}
            <template v-if="similar.candidates.length">
              ({{ similar.candidates.length }})
            </template>
          </p>

          <p
            v-if="similar.loading"
            class="ctc-widget-composer__inline"
            role="status"
          >
            <v-progress-circular
              indeterminate
              size="14"
              width="2"
              color="primary"
            />
            <span>Recherche et vérification des offres…</span>
          </p>
          <p
            v-else-if="similar.error"
            class="ctc-widget-composer__inline ctc-widget-composer__inline--error"
            role="alert"
          >
            <v-icon x-small :color="colors.danger">
              {{ icons.mdiAlertCircleOutline }}
            </v-icon>
            <span>{{ similar.error }}</span>
            <rmr-action-button
              label="Réessayer"
              variant="ghost"
              size="xs"
              @click="similarActions.search()"
            />
          </p>
          <p
            v-else-if="!similar.candidates.length"
            class="ctc-widget-composer__inline"
            role="status"
          >
            <v-icon x-small :color="colors.muted">
              {{ icons.mdiInformationOutline }}
            </v-icon>
            <span>
              Aucun produit avec offre trouvé. Essayez une autre recherche.
            </span>
          </p>
          <ctc-widget-results
            v-else
            :products="similar.candidates"
            :selected-ids="selectedIds"
            :groups="similar.selected"
            :active-index="activeIndex"
            :widget-type="widgetType"
            :can-add-product="canAddProduct"
            :can-add-model="canAddModel"
            @add="addProduct"
          />
        </div>

        <!-- Right: what will be sent, always in view while searching. -->
        <aside
          ref="selectionSection"
          class="ctc-widget-composer__panel"
          aria-labelledby="ctc-selection-title"
        >
          <div class="ctc-widget-composer__sticky">
            <h3
              id="ctc-selection-title"
              ref="selectionHeading"
              tabindex="-1"
              class="ctc-widget-composer__panel-title"
            >
              Votre widget
            </h3>
            <ctc-similar-groups-editor
              :groups="similar.selected"
              :can-add-variant="canAddProduct"
              :widget-type="widgetType"
              :active-index="activeIndex"
              @select="activeIndex = $event"
              @rename-group="similarActions.renameGroup"
              @remove-product="similarActions.remove"
              @remove-group="removeGroup"
              @add-variant="openVariantPicker"
            />
            <p class="ctc-widget-composer__note">{{ selectionHelp }}</p>
          </div>
        </aside>
      </div>
    </template>

    <ctc-variant-picker-modal
      v-model="variantPickerOpen"
      :group="variantGroup"
      :candidates="similar.variantCandidates"
      :loading="similar.variantsLoading"
      :error="similar.variantsError || ''"
      :selected-ids="selectedIds"
      :can-add="canAddProduct"
      @search="similarActions.searchVariants"
      @add="addVariant"
    />
    <v-snackbar
      :value="!!toast"
      timeout="1600"
      :color="colors.toast"
      @input="!$event && (toast = '')"
    >
      {{ toast }}
    </v-snackbar>
  </section>
</template>

<script>
import {
  mdiAlertCircleOutline,
  mdiCheck,
  mdiInformationOutline,
  mdiMagnify,
  mdiViewGridPlusOutline,
} from "@mdi/js";
import { computed, nextTick, ref, watch } from "@vue/composition-api";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrSegmentedControl from "@/components/ui/actions/RmrSegmentedControl.vue";
import {
  RMR_DANGER,
  RMR_INDIGO,
  RMR_TEXT_MUTED,
  RMR_TEXT_PRIMARY,
} from "@/styles/tokens";

import { WIDGET_MAX_MODELS, WIDGET_MAX_PRODUCTS } from "../constants";
import CtcSimilarGroupsEditor from "./CtcSimilarGroupsEditor.vue";
import CtcVariantPickerModal from "./CtcVariantPickerModal.vue";
import CtcWidgetFormatPicker from "./CtcWidgetFormatPicker.vue";
import CtcWidgetResults from "./CtcWidgetResults.vue";

/**
 * Step-2 widget composer: include/exclude, format, then search results on
 * the left and the widget being built on the right.
 */
export default {
  name: "CtcSimilarProductsOption",
  components: {
    CtcSimilarGroupsEditor,
    CtcVariantPickerModal,
    CtcWidgetFormatPicker,
    CtcWidgetResults,
    RmrActionButton,
    RmrSegmentedControl,
  },
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: {
      type: Boolean,
      default: true,
    },
    widgetType: {
      type: String,
      default: "BOX",
    },
    // useCtcCreateFlow().state.similar and its similarActions.
    similar: {
      type: Object,
      required: true,
    },
    similarActions: {
      type: Object,
      required: true,
    },
    // A widget content already exists at Affilizz (verified, then excluded).
    createdWidgetExcluded: {
      type: Boolean,
      default: false,
    },
    // Incremented by "Modifier le widget": bring the selection into view.
    focusSelection: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    const variantPickerOpen = ref(false);
    const variantGroupIndex = ref(null);
    const selectionSection = ref(null);
    const selectionHeading = ref(null);
    // The model the results' main button adds to.
    const activeIndex = ref(Math.max(0, props.similar.selected.length - 1));
    const toast = ref("");

    const selectedIds = computed(() =>
      props.similar.selected.flatMap((group) =>
        group.products.map((product) => product.productLocalizedId)
      )
    );
    const canAddProduct = computed(
      () => selectedIds.value.length < WIDGET_MAX_PRODUCTS
    );

    const showSelection = () =>
      nextTick(() => {
        const reduce =
          typeof window !== "undefined" &&
          window.matchMedia &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (selectionSection.value) {
          selectionSection.value.scrollIntoView({
            behavior: reduce ? "auto" : "smooth",
            block: "start",
          });
        }
        if (selectionHeading.value) {
          selectionHeading.value.focus({ preventScroll: true });
        }
      });

    watch(() => props.focusSelection, showSelection);
    watch(
      () => props.similar.selected.length,
      (length) => {
        if (activeIndex.value > length - 1) {
          activeIndex.value = Math.max(0, length - 1);
        }
        if (
          variantGroupIndex.value !== null &&
          variantGroupIndex.value >= length
        ) {
          variantPickerOpen.value = false;
          variantGroupIndex.value = null;
        }
      }
    );

    // The flow refuses duplicates and limits: only a real change is shown.
    const addProduct = ({ product, groupIndex }) => {
      const before = props.similar.selected.length;
      const count = selectedIds.value.length;
      props.similarActions.add({ product, groupIndex });
      if (selectedIds.value.length === count) return;
      if (groupIndex === null) {
        activeIndex.value = before;
        toast.value = `Nouveau modèle ${before + 1} créé`;
      } else {
        activeIndex.value = groupIndex;
        toast.value = `Ajouté comme coloris du modèle ${groupIndex + 1}`;
      }
    };

    return {
      activeIndex,
      addProduct,
      addVariant: (product) =>
        props.similarActions.add({
          product,
          groupIndex: variantGroupIndex.value,
        }),
      canAddModel: computed(
        () =>
          props.similar.selected.length < WIDGET_MAX_MODELS &&
          canAddProduct.value
      ),
      canAddProduct,
      colors: {
        danger: RMR_DANGER,
        indigo: RMR_INDIGO,
        muted: RMR_TEXT_MUTED,
        toast: RMR_TEXT_PRIMARY,
      },
      disabledText: computed(() =>
        props.createdWidgetExcluded
          ? "Exclu de l’article. Le widget déjà créé chez Affilizz n’est ni recréé ni supprimé."
          : "Aucun widget ne sera intégré dans l’article."
      ),
      icons: {
        mdiAlertCircleOutline,
        mdiInformationOutline,
        mdiMagnify,
        mdiViewGridPlusOutline,
      },
      includedText: computed(() =>
        props.widgetType === "CARD"
          ? "Carte produit incluse dans l’article"
          : "Comparateur de prix inclus dans l’article"
      ),
      inclusionItems: [
        { value: true, label: "Inclure", icon: mdiCheck },
        { value: false, label: "Exclure" },
      ],
      openVariantPicker: (index) => {
        variantGroupIndex.value = index;
        variantPickerOpen.value = true;
      },
      // A whole model goes through the same per-product removal.
      removeGroup: (index) => {
        const group = props.similar.selected[index];
        if (!group) return;
        group.products
          .map((product) => product.productLocalizedId)
          .forEach((id) => props.similarActions.remove(id));
        toast.value = `Modèle ${index + 1} retiré`;
      },
      selectedIds,
      selectionHeading,
      selectionHelp: computed(() =>
        props.widgetType === "CARD"
          ? "Tous ces produits sont réunis dans une seule carte, sans onglet. Les noms de modèle ne sont pas affichés."
          : "Chaque modèle devient un onglet Affilizz. Cliquez sur un titre pour le renommer."
      ),
      selectionSection,
      showSelection,
      toast,
      variantGroup: computed(
        () => props.similar.selected[variantGroupIndex.value] || null
      ),
      variantPickerOpen,
    };
  },
};
</script>

<style lang="scss" scoped>
// No overflow clip: the widget panel sticks inside the step while searching.
.ctc-widget-composer {
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-2xl;
  background: $rmr-surface-0;
}

.ctc-widget-composer__header {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
}

.ctc-widget-composer__icon {
  display: grid;
  width: 38px;
  height: 38px;
  flex: none;
  place-items: center;
  border-radius: $rmr-radius-md;
  background: $rmr-indigo-bg;
}

.ctc-widget-composer__copy {
  min-width: 0;
  flex: 1;

  strong,
  small {
    display: block;
  }

  strong {
    color: $rmr-text-primary;
    font-size: $rmr-fs-title;
  }

  small {
    margin-top: 2px;
    color: $rmr-text-muted;
    font-size: $rmr-fs-md;
  }
}

.ctc-widget-composer__toolbar {
  padding: 12px 20px;
  border-top: 1px solid $rmr-border;
  border-bottom: 1px solid $rmr-border;
  background: $rmr-surface-2;
}

// Results | the widget. The panel column has a fixed, readable width.
.ctc-widget-composer__cols {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
}

.ctc-widget-composer__search-col {
  min-width: 0;
  padding: 20px;
  border-right: 1px solid $rmr-border;
}

.ctc-widget-composer__search {
  display: flex;
  gap: 8px;
}

.ctc-widget-composer__field {
  display: flex;
  min-width: 0;
  height: 42px;
  flex: 1;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-md;
  background: $rmr-surface-0;
  cursor: text;

  &:focus-within {
    border-color: $rmr-indigo;
    box-shadow: 0 0 0 3px $rmr-indigo-bg;
  }

  input {
    min-width: 0;
    flex: 1;
    border: 0;
    background: transparent;
    color: $rmr-text-primary;
    font-size: $rmr-fs-body;
    outline: 0;
  }
}

.ctc-widget-composer__search ::v-deep .v-btn {
  height: 42px !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.ctc-widget-composer__source {
  display: inline-block;
  margin: 14px 0 0;
  padding: 5px 12px;
  border-radius: $rmr-radius-pill;
  background: $rmr-text-primary;
  color: $rmr-surface-0;
  font-size: $rmr-fs-md;
  font-weight: 600;
}

.ctc-widget-composer__inline {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 12px 0 0;
  color: $rmr-text-secondary;
  font-size: $rmr-fs-md;

  span {
    min-width: 0;
    flex: 1 1 auto;
  }
}

.ctc-widget-composer__inline--error {
  color: $rmr-danger;
}

.ctc-widget-composer__panel {
  min-width: 0;
  padding: 20px;
  border-radius: 0 0 $rmr-radius-2xl 0;
  background: $rmr-surface-2;
}

// Sticks to the viewport inside its own column: the page keeps scrolling.
.ctc-widget-composer__sticky {
  position: sticky;
  top: 16px;
}

.ctc-widget-composer__panel-title {
  margin: 0;
  color: $rmr-text-primary;
  font-size: $rmr-fs-title;
  font-weight: 700;

  &:focus-visible {
    border-radius: 4px;
    outline: 2px solid $rmr-indigo;
    outline-offset: 2px;
  }
}

.ctc-widget-composer__note {
  margin: 10px 0 0;
  color: $rmr-text-subtle;
  font-size: $rmr-fs-base;
  text-align: center;
}

// Narrow: one column, the widget first, nothing sticky.
@media (max-width: 980px) {
  .ctc-widget-composer__cols {
    grid-template-columns: minmax(0, 1fr);
  }

  .ctc-widget-composer__search-col {
    border-right: 0;
  }

  .ctc-widget-composer__panel {
    order: -1;
    border-bottom: 1px solid $rmr-border;
    border-radius: 0;
  }

  .ctc-widget-composer__sticky {
    position: static;
  }
}

@media (max-width: 599px) {
  .ctc-widget-composer__header {
    flex-wrap: wrap;
    padding: 14px 16px;
  }

  .ctc-widget-composer__copy {
    flex-basis: calc(100% - 52px);
  }

  .ctc-widget-composer__header .rmr-segmented {
    margin-left: 52px;
  }

  .ctc-widget-composer__toolbar,
  .ctc-widget-composer__search-col,
  .ctc-widget-composer__panel {
    padding-right: 16px;
    padding-left: 16px;
  }
}
</style>
